// Where an image's subject actually sits within its canvas.
//
// Entity art is framed for the isometric board — a figure occupying a small part
// of a large canvas, with headroom above and the tile's footprint below — so it
// renders as a speck when dropped into a UI disc. Recording the subject's extent
// at upload time lets the client crop to it.
//
// The client can and does derive this itself by scanning the alpha channel, so
// this is a precomputation, never a requirement: an image with no recorded bounds
// still crops correctly, just at the cost of a scan and to within its sampling
// stride.

export interface NormalisedBounds {
    // Fractions of the image's size, measured from the BOTTOM-LEFT corner.
    x: number;
    y: number;
    w: number;
    h: number;
}

// Alpha at or above which a pixel counts as part of the subject. Low on purpose:
// the aim is to avoid clipping anything a player can see, and a feathered edge is
// still visible. Mirrors TRIM_ALPHA_THRESHOLD in RemoteConstants.cs. (Contrast
// silhouette.ts, which traces an outline and wants a firm edge at 128.)
export const SUBJECT_ALPHA_THRESHOLD = 8;

/**
 * The subject's bounding box, or undefined when the image is entirely
 * transparent — which is a broken asset rather than something to record.
 *
 * The RAW extent, with no breathing room added: the margin left around it when
 * drawing belongs to the client (see TRIM_PADDING), so that retuning how large
 * icons render never means re-recording this.
 *
 * Note the flip. ImageData rows run TOP-DOWN, while a Unity texture — and so the
 * stored value — is measured from the bottom. Getting that backwards produces a
 * vertically mirrored crop that still looks plausible on a roughly symmetric
 * subject, so it is covered by tests rather than by inspection.
 */
export function opaqueBounds(
    pixels: ImageData,
    alphaThreshold: number = SUBJECT_ALPHA_THRESHOLD,
): NormalisedBounds | undefined {
    const {width, height, data} = pixels;
    if (width <= 0 || height <= 0) return undefined;

    let minX = width, maxX = -1;
    let minTop = height, maxTop = -1;      // in top-down row coordinates

    for (let y = 0; y < height; y++) {
        const row = y * width * 4;
        for (let x = 0; x < width; x++) {
            if (data[row + x * 4 + 3] < alphaThreshold) continue;

            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minTop) minTop = y;
            if (y > maxTop) maxTop = y;
        }
    }

    if (maxX < minX || maxTop < minTop) return undefined;    // nothing visible

    // Top-down row `y` is bottom-up row `height - 1 - y`, so the LOWEST edge of the
    // box comes from the LAST row that saw the subject.
    const bottom = height - 1 - maxTop;

    return {
        x: minX / width,
        y: bottom / height,
        w: (maxX - minX + 1) / width,
        h: (maxTop - minTop + 1) / height,
    };
}

/**
 * Read a locally-selected image file's pixels. No network and no CORS concerns —
 * the bytes are already in hand — which is why recording the bounds at upload
 * time is essentially free.
 *
 * Returns undefined if the file can't be decoded as an image.
 */
export async function readImagePixels(file: File): Promise<ImageData | undefined> {
    try {
        const bitmap = await createImageBitmap(file);
        const canvas = document.createElement('canvas');
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;

        const context = canvas.getContext('2d', {willReadFrequently: true});
        if (context === null) {
            bitmap.close();
            return undefined;
        }

        context.drawImage(bitmap, 0, 0);
        bitmap.close();
        return context.getImageData(0, 0, canvas.width, canvas.height);
    } catch {
        return undefined;
    }
}

/**
 * Derive the subject bounds of an ALREADY-UPLOADED image, for showing what the
 * client will crop to. Needs a crossOrigin load to read the pixels back, so it is
 * kept out of any render path — a host without CORS headers taints the canvas and
 * this simply answers undefined, exactly as the Workshop's sprite analysis does.
 */
export function deriveBoundsFromUrl(url: string): Promise<NormalisedBounds | undefined> {
    return new Promise(resolve => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => {
            const width = image.naturalWidth;
            const height = image.naturalHeight;
            if (width <= 0 || height <= 0) {
                resolve(undefined);
                return;
            }

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const context = canvas.getContext('2d', {willReadFrequently: true});
            if (context === null) {
                resolve(undefined);
                return;
            }

            context.drawImage(image, 0, 0);
            try {
                resolve(opaqueBounds(context.getImageData(0, 0, width, height)));
            } catch {
                resolve(undefined);      // tainted canvas
            }
        };
        image.onerror = () => resolve(undefined);
        image.src = url;
    });
}
